import React, { useState, useRef, useEffect } from "react";
import { Camera, ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from 'react-router-dom';

const LLDetectorCamera = () => {
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { productInfo } = location.state || {};
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera access error:", err);
      setError("Failed to access camera");
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
  };

  const takePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageUrl = canvas.toDataURL("image/png");
      setCapturedImage(imageUrl);
      stopCamera();
      navigate('/light-leakage-labeling', { 
        state: { 
          productInfo, 
          imageUrl 
        } 
      });
    }
  };

  const handleBack = () => {
    stopCamera();
    navigate('/light-leakage-product-info', { state: { productInfo } });
  };

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden relative bg-black">
      {!capturedImage ? (
        <>
          <video
            ref={videoRef}
            className="absolute top-0 left-0 w-full h-full object-cover z-0"
            autoPlay
            playsInline
          />
          <canvas ref={canvasRef} className="hidden" />
          {/* Top Border Overlay */}
          <div className="absolute top-0 left-0 w-full h-16 bg-black bg-opacity-70 border-b-4 border-white z-10">
            <div className="text-gray-300 text-xl italic p-4 pl-12">
              Light Leakage Detector Camera
            </div>
          </div>
        </>
      ) : (
        <img
          src={capturedImage}
          alt="Captured"
          className="absolute top-0 left-0 w-full h-full object-cover z-0"
        />
      )}

      <div className="absolute top-4 left-4 z-20">
        <button onClick={handleBack} className="text-white">
          <ArrowLeft className="w-6 h-6" />
        </button>
      </div>

      {error && (
        <div className="absolute top-20 left-4 text-red-500 text-sm z-20">
          {error}
        </div>
      )}

      {!capturedImage && (
        <div className="absolute bottom-0 w-full h-16 bg-black bg-opacity-70 border-t-4 border-white flex justify-center items-center text-white z-10">
          <button
            onClick={takePhoto}
            className="w-12 h-12 rounded-full border-4 border-white flex items-center justify-center transition duration-300 hover:opacity-80"
            title="Take Photo"
          >
            <Camera size={24} />
          </button>
        </div>
      )}
    </div>
  );
};

export default LLDetectorCamera;